import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import adminAgentsService from '@/services/adminAgents.service';
import sectorsService from '@/services/sectors.service';
import { useToast } from '@/contexts/ToastContext';
import { AdminAgent, AdminAgentFilters } from '@/types/adminAgent';
import { PublicSector } from '@/types/agents';

function emptyFilters(): AdminAgentFilters {
  return { scope: '', type: '', owner_inactive: '', is_active: '', sector_id: '' };
}

function StatusBadge({ agent }: { agent: AdminAgent }) {
  if (!agent.is_active) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-300 border border-red-500/30">
        inativo
      </span>
    );
  }
  if (agent.is_published) {
    return (
      <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-300 border border-green-500/30">
        publicado
      </span>
    );
  }
  return (
    <span className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-300 border border-amber-500/30">
      rascunho
    </span>
  );
}

const AdminAgentsPage: FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialFilters = useMemo<AdminAgentFilters>(() => {
    const base = emptyFilters();
    (Object.keys(base) as Array<keyof AdminAgentFilters>).forEach((k) => {
      const v = searchParams.get(k as string);
      if (v) (base as Record<string, string>)[k as string] = v;
    });
    return base;
  }, [searchParams]);

  const [filters, setFilters] = useState<AdminAgentFilters>(initialFilters);
  const [agents, setAgents] = useState<AdminAgent[]>([]);
  const [sectors, setSectors] = useState<PublicSector[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAgentsService.list(filters);
      setAgents(data);
    } catch {
      showToast('Não foi possível carregar os agentes.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    sectorsService.list().then(setSectors).catch(() => undefined);
  }, []);

  const updateFilter = (key: keyof AdminAgentFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key as string, value);
    else next.delete(key as string);
    setSearchParams(next, { replace: true });
  };

  const handleAction = async (
    id: string,
    op: 'publish' | 'unpublish' | 'activate' | 'deactivate',
  ) => {
    try {
      await adminAgentsService[op](id);
      showToast('Atualizado.', 'success');
      void refresh();
    } catch {
      showToast('Erro ao executar ação.', 'error');
    }
  };

  const handleDelete = async (agent: AdminAgent) => {
    if (!window.confirm(`Excluir "${agent.name}"? Essa ação remove conversas e arquivos.`)) return;
    try {
      await adminAgentsService.remove(agent.id);
      showToast('Agente excluído.', 'success');
      void refresh();
    } catch {
      showToast('Erro ao excluir.', 'error');
    }
  };

  const hasInactiveOwner = agents.some((a) => a.owner_is_active === false);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-agents-page">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Agentes</h1>
          <p className="text-sm text-content-tertiary mt-1">
            Gestão completa dos agentes do Hub.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/agentes/novo')}
          className="inline-flex items-center gap-1 bg-accent text-surface-primary font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition"
          data-testid="new-agent"
        >
          <span className="material-symbols-outlined text-base">add</span>
          Novo agente
        </button>
      </header>

      {hasInactiveOwner && filters.owner_inactive !== '1' && (
        <div
          data-testid="owner-inactive-banner"
          className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 flex items-center justify-between"
        >
          <span>
            ⚠ Existem agentes cujos donos foram desativados — vale revisar pra reatribuir ou
            desativar o agente.
          </span>
          <button
            type="button"
            onClick={() => updateFilter('owner_inactive', '1')}
            className="font-semibold underline"
          >
            Ver lista
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <select
          data-testid="filter-scope"
          value={filters.scope || ''}
          onChange={(e) => updateFilter('scope', e.target.value)}
          className="rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-sm text-content-primary"
        >
          <option value="">Todos escopos</option>
          <option value="PERSONAL">Pessoal</option>
          <option value="SECTOR">Setor</option>
          <option value="GLOBAL">Global</option>
        </select>
        <select
          data-testid="filter-type"
          value={filters.type || ''}
          onChange={(e) => updateFilter('type', e.target.value)}
          className="rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-sm text-content-primary"
        >
          <option value="">Todos tipos</option>
          <option value="RAG">RAG</option>
          <option value="SQL">SQL</option>
        </select>
        <select
          data-testid="filter-sector"
          value={filters.sector_id || ''}
          onChange={(e) => updateFilter('sector_id', e.target.value)}
          className="rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-sm text-content-primary"
        >
          <option value="">Todos setores</option>
          {sectors.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-content-secondary px-3">
          <input
            type="checkbox"
            data-testid="filter-owner-inactive"
            checked={filters.owner_inactive === '1'}
            onChange={(e) => updateFilter('owner_inactive', e.target.checked ? '1' : '')}
            className="accent-accent"
          />
          Só com dono inativo
        </label>
      </div>

      <div className="rounded-xl border border-surface-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-tertiary text-content-secondary text-left">
            <tr>
              <th className="px-3 py-2">Nome</th>
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">Escopo</th>
              <th className="px-3 py-2">Owner</th>
              <th className="px-3 py-2">Setores</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Arquivos</th>
              <th className="px-3 py-2">Convs 30d</th>
              <th className="px-3 py-2 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-content-tertiary">
                  Carregando…
                </td>
              </tr>
            )}
            {!loading && agents.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-content-tertiary">
                  Nenhum agente.
                </td>
              </tr>
            )}
            {!loading &&
              agents.map((agent) => (
                <tr
                  key={agent.id}
                  data-testid="agent-row"
                  className="border-t border-surface-border hover:bg-surface-hover/30"
                >
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/agentes/${agent.id}/editar`)}
                      className="text-accent hover:underline font-medium flex items-center gap-1"
                    >
                      <span>{agent.avatar || '🤖'}</span>
                      {agent.name}
                    </button>
                  </td>
                  <td className="px-3 py-2">{agent.type}</td>
                  <td className="px-3 py-2">{agent.scope}</td>
                  <td className="px-3 py-2">
                    <span className="text-content-secondary">{agent.owner_name || '—'}</span>
                    {agent.owner_is_active === false && (
                      <span className="ml-1 text-2xs uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 border border-red-500/30">
                        inativo
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {agent.sectors.slice(0, 2).map((s) => (
                        <span
                          key={s.id}
                          className="px-1.5 py-0.5 rounded bg-surface-tertiary text-xs text-content-secondary"
                        >
                          {s.name}
                        </span>
                      ))}
                      {agent.sectors.length > 2 && (
                        <span className="text-xs text-content-tertiary">
                          +{agent.sectors.length - 2}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge agent={agent} />
                  </td>
                  <td className="px-3 py-2 text-content-secondary">{agent.file_count}</td>
                  <td className="px-3 py-2 text-content-secondary">
                    {agent.conversation_count_30d}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/agentes/${agent.id}/editar`)}
                        className="text-xs text-accent hover:underline"
                      >
                        Editar
                      </button>
                      {agent.is_published ? (
                        <button
                          type="button"
                          onClick={() => handleAction(agent.id, 'unpublish')}
                          className="text-xs text-content-secondary hover:text-content-primary"
                        >
                          Despublicar
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleAction(agent.id, 'publish')}
                          className="text-xs text-green-400 hover:underline"
                        >
                          Publicar
                        </button>
                      )}
                      {agent.is_active ? (
                        <button
                          type="button"
                          onClick={() => handleAction(agent.id, 'deactivate')}
                          className="text-xs text-amber-400 hover:underline"
                        >
                          Desativar
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleAction(agent.id, 'activate')}
                          className="text-xs text-green-400 hover:underline"
                        >
                          Ativar
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDelete(agent)}
                        className="text-xs text-red-400 hover:underline"
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAgentsPage;
